import { useTotalSpotAvailableValue } from 'hooks/spot/useTotalSpotAvailableValue'
import { useTotalPerpetualAvailableMarginValue } from 'hooks/perp/useTotalAvailableMarginValue'
import { usePositionValue } from 'hooks/perp/usePositionValue'
import { useAccountLeverage } from 'hooks/perp/useAccountLeverage'
import { useRiskRatio } from 'hooks/perp/useRiskRatio'
import { useTotalEquityValue } from 'hooks/perp/useTotalEquityValue'
import { useRealTimeUnrealizedPnL } from 'hooks/perp/useRealTimeUnrealizedPnL'
import { TrendColorizerText } from 'components/TrendColorizer'
import { EFavoriteAssetsElements } from 'store/settings/types'
import {
  PRIMARY_CURRENCY,
  PRIMARY_CURRENCY_DECIMALS,
} from 'constants/currencies'
import { toFixed, toGrouping, w2e } from 'utils/number'
import numbro from 'numbro'
import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'
import { styled } from '@mui/material/styles'
import { PERP_DEFAULT_MARKET } from 'constants/trade'
import { MaskedAssets } from 'components/MaskedAssets'

enum RiskLevel {
  Low,
  Medium,
  High,
  Default,
}

export const useAccountAssetList = () => {
  const totalAvailableUSDValue = useTotalSpotAvailableValue()
  const { t } = useTranslation()
  const availableMargin = useTotalPerpetualAvailableMarginValue()
  const positionValue = usePositionValue()
  const accountLeverage = useAccountLeverage()
  const equityValue = useTotalEquityValue()
  const unrealizedPnL = useRealTimeUnrealizedPnL()
  const risk = useRiskRatio()
  const level = getRiskLevel(risk)

  const StyledRiskColor = styled('span')<{
    $level?: RiskLevel
  }>`
    color: ${({ $level, theme }) =>
      $level === RiskLevel.High
        ? theme.palette.error.main
        : $level === RiskLevel.Medium
          ? theme.palette.warning.main
          : $level === RiskLevel.Low
            ? theme.palette.success.main
            : ''};
  `

  function getRiskLevel(risk: number | string) {
    risk = Number(risk)
    if (isNaN(risk)) {
      return RiskLevel.Default
    }
    if (risk === 0) {
      return RiskLevel.Default
    }

    if (risk <= 0.2) {
      return RiskLevel.Low
    } else if (risk > 0.2 && risk <= 0.5) {
      return RiskLevel.Medium
    } else if (risk > 0.5) {
      return RiskLevel.High
    } else {
      return RiskLevel.Low
    }
  }

  const defaultPerpUrl = `/perp/${PERP_DEFAULT_MARKET}/${PRIMARY_CURRENCY}`

  const memoAssetsList = useMemo(() => {
    return [
      {
        title: 'Est. Balance',
        value: `$${toGrouping(w2e(totalAvailableUSDValue))}`,
        type: EFavoriteAssetsElements.Balance,
        to: '/portfolio/assets',
      },
      {
        title: 'Total Margin',
        value: `$${toGrouping(w2e(equityValue))}`,
        type: EFavoriteAssetsElements.TotalMargin,
        to: defaultPerpUrl,
      },
      {
        title: 'Available Margin',
        value: `$${toGrouping(w2e(availableMargin))}`,
        type: EFavoriteAssetsElements.AvailableMargin,
        to: defaultPerpUrl,
      },
      {
        title: 'Position Value',
        value: `$${toFixed(w2e(positionValue), PRIMARY_CURRENCY_DECIMALS, {
          fixed: true,
        })}`,
        type: EFavoriteAssetsElements.PositionValue,
        to: defaultPerpUrl,
      },
      {
        title: 'Unrealized PnL',
        render: () => {
          const dir =
            Number(w2e(unrealizedPnL)) === 0
              ? null
              : Number(w2e(unrealizedPnL)) > 0
          return (
            <MaskedAssets>
              <TrendColorizerText $trend={dir}>
                {`${toFixed(w2e(unrealizedPnL), PRIMARY_CURRENCY_DECIMALS, {
                  fixed: true,
                })} ${PRIMARY_CURRENCY}`}
              </TrendColorizerText>
            </MaskedAssets>
          )
        },
        type: EFavoriteAssetsElements.UnPnl,
        to: defaultPerpUrl,
      },
      {
        title: 'Account Leverage',
        value: `${toFixed(w2e(accountLeverage), 4, { fixed: true })}x`,
        type: EFavoriteAssetsElements.Leverage,
        to: defaultPerpUrl,
      },
      {
        title: 'Risk',
        type: EFavoriteAssetsElements.Risk,
        render: () => {
          return (
            <StyledRiskColor $level={level}>
              {`${numbro(risk)
                .format({ output: 'percent', mantissa: 2 })
                .toString()}`}
            </StyledRiskColor>
          )
        },
        to: defaultPerpUrl,
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    t,
    totalAvailableUSDValue,
    equityValue,
    availableMargin,
    positionValue,
    unrealizedPnL,
    accountLeverage,
    risk,
    level,
  ])

  return {
    memoAssetsList,
  }
}
